import { createGtm } from '@gtm-support/vue-gtm'
import { BbkCustomerDataLayer, CartItem } from '~/graphql/types/graphql'
import type { ExtendedCategoryDataLayer } from '~/types/dataLayer'
import { getProductForGtm } from '~/utils/getProductForGtm'
import { useGlobalStore } from '~/store/global'
import type { ProductBaseGtm } from '~/types/productBaseGtm'
import type { ProductBase } from '~/types/productBase'

export default defineNuxtPlugin(({ vueApp, hook }) => {
    const { appName, googleTagManagerId } = useRuntimeConfig().public
    const { $bus } = useNuxtApp()

    const { CURRENCY_SYMBOL_PLN } = useAppConfig()
    const currentStore = useGlobalStore().storeName
    const gaCookie = useCookie('_ga').value

    if (appName !== 'production') {
        return
    }

    vueApp.use(
        createGtm({
            id: googleTagManagerId,
            defer: false,
            compatibility: false,
            enabled: true,
            debug: false,
            loadScript: true,
            trackOnNextTick: false,
        }),
    )

    $bus.$on('wishlistAddProductsSuccess', ({ product }) => {
        useGtmAddToWishlist(product)
    })

    $bus.$on('productTileClickEvent', ({ product, ...params }) => {
        const { productClickEvent } = useGtmCategory()

        productClickEvent({ product: getProductForGtm(product), ...params })
    })

    $bus.$on(
        'productPageLoaded',
        async ({
            product,
            parent,
            isConfigParent,
            gaCookie,
            currentStore,
            customerData,
        }: {
            product: ProductBase
            isConfigParent: boolean
            productRelated: ReturnType<typeof getProductRelatedForGtm>[]
            gaCookie: string
            currentStore: string
            customerData: BbkCustomerDataLayer
        }) => {
            const { productPageLoadedEvent } = useGtmProduct()
            await productPageLoadedEvent(product, isConfigParent, parent, gaCookie, currentStore, customerData)
        },
    )

    $bus.$on(
        'groupedProductLoaded',
        async ({
             parentProduct,
             product,
             gaCookie,
             currentStore,
             customerData,
         }: {
            parentProduct: [ProductBase]
            product: [ProductBase]
            gaCookie: string
            currentStore: string
            customerData: BbkCustomerDataLayer
        }) => {
            const { groupedProductLoadedEvent } = useGtmProduct()
            await groupedProductLoadedEvent(parentProduct, product, gaCookie, currentStore, customerData)
        },
    )

    $bus.$on('relatedLocalProductLoaded', ({ products }) => {
        const { relatedProductLoadedEvent } = useGtmProduct()
        products && relatedProductLoadedEvent({ products })
    })

    $bus.$on('relatedProductLoaded', ({ products, campaignId, experienceId }) => {
        if (products) {
            const { relatedProductLoadedEvent } = useGtmProduct()
            relatedProductLoadedEvent({ products, eventOrigin: 'cw', campaignId, experienceId })
        }
    })

    $bus.$on('carouselProductsLoaded', ({products, experienceId}) => {
        if ( products ) {
            const { carouselProductPageLoadedEvent } = useGtmProduct()
            carouselProductPageLoadedEvent(products, experienceId)
        }
    })

    $bus.$on(
        'catalogPageLoaded',
        async ({
            name,
            items,
            dataLayer,
            currentStore,
            customerData,
            gaCookie,
        }: {
            name: string
            items: ProductBaseGtm[]
            dataLayer: ExtendedCategoryDataLayer
            currentStore: string
            customerData: BbkCustomerDataLayer
            gaCookie: string
        }) => {
            const { pageTypeEvent, productsLoadedEvent, categoryDataEvent, fireRemarketingTagEvent } = useGtmCategory({
                categoryName: name,
            })
            pageTypeEvent(currentStore, customerData, gaCookie)
            categoryDataEvent(dataLayer, name)
            productsLoadedEvent(items)
            await fireRemarketingTagEvent()
        },
    )

    $bus.$on('catalogUpdateProduct', ({ name, items }) => {
        const { productsUpdateEvent } = useGtmCategory({
            categoryName: name,
        })
        productsUpdateEvent(items)
    })

    $bus.$on(
        'searchPageLoaded',
        async ({
            items,
            currentStore,
            customerData,
            gaCookie,
        }: {
            currentStore: string
            customerData: BbkCustomerDataLayer
            gaCookie: string
            items: ProductBaseGtm[]
        }) => {
            const { searchEventsInGtm, searchProduct } = useGtmSearch()

            window.dataLayer.push({
                pageType: 'search'
            })

            searchProduct(items)
            await searchEventsInGtm(currentStore, customerData, gaCookie)
        },
    )

    $bus.$on('searchProductUpdated', ({ items }) => {
        const { searchProductUpdated } = useGtmSearch()
        searchProductUpdated(items)
    })

    $bus.$on('addToCartSuccess', ({ product, quantity, parent, groupedItems = null }) => {
        const { addToCart, addGroupedProductToCart } = useGtmCart()

        if ( groupedItems.length > 1 ) {
            groupedItems && addGroupedProductToCart(product.sku, groupedItems, CURRENCY_SYMBOL_PLN, currentStore)
        } else {
            product && addToCart(getProductForGtm(product, quantity), parent, CURRENCY_SYMBOL_PLN, currentStore)
        }
    })

    $bus.$on('cartProductUpdate', ({ payload, quantity: value }) => {
        if (!payload) return

        const { addToCart, factoryProduct, removeFromCart } = useGtmCart()
        const quantity = value - payload.quantity

        if (quantity < 0) {
            removeFromCart(
                factoryProduct({ ...payload, quantity: Math.abs(quantity) }),
                CURRENCY_SYMBOL_PLN,
                currentStore,
            )
        } else {
            addToCart(factoryProduct({ ...payload, quantity }), payload.parent, CURRENCY_SYMBOL_PLN, currentStore)
        }
    })
    $bus.$on('cartProductRemove', async ({ payload }) => {
        if (!payload) return

        const { removeFromCart, factoryProduct } = useGtmCart()

        removeFromCart(factoryProduct(payload), CURRENCY_SYMBOL_PLN, currentStore)
    })

    $bus.$on(
        'cartPageLoaded',
        ({
            marketplace,
            totals: {
                total: basketTotal,
                currency: { name: currentCode },
            },
            customerData,
            ...data
        }) => {
            if (!marketplace) return

            const { checkout, factoryProduct, checkCartType } = useGtmCart()

            const parsedProduct = marketplace.reduce(
                (acc, payload) => {
                    if (payload) {
                        const products = payload.items?.reduce((acc, item) => {
                            if (item) {
                                acc.push(item)
                            }

                            return acc
                        }, [] as CartItem[])
                        if (products) {
                            acc.push(...products.map(factoryProduct))
                        }
                    }

                    return acc
                },
                [] as ReturnType<typeof factoryProduct>[],
            )

            const cartType = checkCartType(marketplace)

            checkout({
                products: parsedProduct,
                basketTotal,
                currentCode,
                cartType,
                ...data,
            })

            useGtm()?.trackEvent({
                event: '',
                noninteraction: false,
                customerData,
                currentStore,
            })
        },
    )

    $bus.$on('customerLoginSuccess', ({ customerId }) => {
        const { loginEvent } = useGtmAuth()

        loginEvent(customerId)
    })

    $bus.$on('customerCreateSuccess', ({ phoneNumber, email, newsletterAgreements }: { email?: string; phoneNumber?: string; newsletterAgreements: [string] }) => {
        const { registerEvent, newsletterSignUpEvent } = useGtmAuth()

        const emailToEvent = newsletterAgreements.includes('email') ? email : undefined;
        const phoneToEvent = newsletterAgreements.includes('sms') ? phoneNumber : undefined;

        registerEvent()
        newsletterSignUpEvent({ email: emailToEvent, phone: phoneToEvent })
    })

    $bus.$on('newsletterSignupSuccess', ({ email, phone }: { email?: string; phone?: string }) => {
        const { newsletterSignUpEvent } = useGtmAuth()

        newsletterSignUpEvent({ email, phone })
    })

    $bus.$on(
        'checkAvailabilityOpen',
        ({ productId, sku, name, brand }: { productId: string; sku: string; name: string; brand: string }) => {
            checkAvailabilityEvent(productId, sku, name, brand)
        },
    )

    useGtmCookies()

    hook('page:start', () => {
        if (window) window?.dataLayer?.splice(0, window?.dataLayer.length ?? 0)
    })
})
